/**
 * Preview access gate — single source of truth for the build-time bypass key.
 *
 * Security level: "friends and family preview", NOT real authentication.
 * The key is baked into the JS bundle at build time via import.meta.env,
 * so anyone who downloads the bundle can theoretically extract it. This is
 * acceptable for a pre-launch gate; it is NOT an authorization boundary for
 * anything sensitive. Real access control remains on Supabase RLS and auth.
 */

const STORAGE_KEY = 'contruly_preview';
const GRANTED_VALUE = 'granted';

export function hasPreviewAccess(): boolean {
  // Local dev (`npm run dev`) always has access so the gate never blocks
  // active development. Production builds (`npm run build`, deployed to
  // Vercel) and `npm run preview` still enforce the localStorage check.
  if (import.meta.env.DEV) return true;
  try {
    return localStorage.getItem(STORAGE_KEY) === GRANTED_VALUE;
  } catch {
    return false;
  }
}

/**
 * What the pre-launch gate does with one visit. Two ways through: the preview
 * flag, or a signed-in session. A creator who opens their own Preview Packages,
 * website or demo booking from the dashboard must not land on the waitlist
 * (2026-10-09, THE_LIST T0 #21, first half; opening the pages to the public
 * waits for the payment step). While the session is still being read, wait,
 * so a signed-in creator is never sent away on the first frame.
 */
export function routeGateDecision(visit: {
  previewAccess: boolean;
  signedIn: boolean;
  authLoading: boolean;
}): 'pass' | 'wait' | 'redirect' {
  if (visit.previewAccess || visit.signedIn) return 'pass';
  return visit.authLoading ? 'wait' : 'redirect';
}

/**
 * Handles open to the public before launch. Jota, 2026-11-02: yes to opening his
 * own page first; no customer is contacted. noindex stays on these pages.
 */
export const PUBLIC_HANDLES: readonly string[] = ['joaopedroreis'];

/** Pages whose access is a token in the link. The token is the credential, and a client must always reach them. */
const TOKEN_ROUTES = [/^\/portal(\/|$)/, /^\/booking-confirmation(\/|$)/, /^\/delivery\//, /^\/crew\//];

/** Paths that name a handle: /book/h, /embed/h/a, /site-preview/h, /h/for/a, /rates/h. */
const HANDLE_ROUTES = [
  /^\/book\/([^/]+)/,
  /^\/embed\/([^/]+)/,
  /^\/site-preview\/([^/]+)/,
  /^\/rates\/([^/]+)/,
  /^\/([^/]+)\/for\//,
];

/** Is this path open to anyone, before launch? Pure, so a test can pin it. */
export function isPublicPath(pathname: string): boolean {
  if (TOKEN_ROUTES.some((re) => re.test(pathname))) return true;
  for (const re of HANDLE_ROUTES) {
    const m = pathname.match(re);
    if (m) return PUBLIC_HANDLES.includes(decodeURIComponent(m[1]).toLowerCase());
  }
  return false;
}

export function grantPreviewAccess(key: string): boolean {
  const expected = import.meta.env.VITE_PREVIEW_KEY as string | undefined;
  if (!expected || !key || key !== expected) return false;
  try {
    localStorage.setItem(STORAGE_KEY, GRANTED_VALUE);
    return true;
  } catch {
    return false;
  }
}

export function revokePreviewAccess(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
